import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { IconPanelLeftClose } from "@/assets/icons/geist/IconPanelLeftClose";
import { IconPanelLeftOpen } from "@/assets/icons/geist/IconPanelLeftOpen";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import { PREDEFINED_TASKS } from "@/components/ai/ai-constants";
import { AgentExecutionActiveEnvironment } from "@/components/ai/chat/AgentExecutionActiveEnvironment";
import { ChatInput, type NewPendingMessage } from "@/components/ai/chat/ChatInput";
import ChatInputWithPrompts from "@/components/ai/chat/ChatInputWithPrompts";
import { ChatMessages } from "@/components/ai/chat/ChatMessages";
import { ScrollToBottomButton } from "@/components/ai/chat/ScrollToBottomButton";
import { TodoList, type TodoItem } from "@/components/ai/chat/TodoList";
import { StopButton } from "@/components/ai/chat/TodoListHeader";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { ScrollArea, type ScrollAreaHandle } from "@/components/flexkit/ScrollArea";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Switch } from "@/components/Switch";
import { Tooltip } from "@/components/Tooltip";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { useAISidebarToggleShortcut } from "@/hooks/use-ai-sidebar-toggle-shortcut";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { useFlagTodoGroupingEnabled } from "@/hooks/use-flag";
import type { FollowModeState } from "@/hooks/use-follow-mode";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useOpenEditorShortcut } from "@/hooks/use-open-editor-shortcut";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import {
    useStreamConversation,
    type AgentMessageFragment,
    type AgentMessageFragmentThinking,
    type AgentMessageFragmentTodoItem,
    type Message,
    type UserMessage,
} from "@/hooks/use-stream-conversation";
import { useStuckConversationDetection } from "@/hooks/use-stuck-conversation-detection";
import { useTheme } from "@/hooks/use-theme";
import { useVSCodeIpcContext } from "@/hooks/use-vscode-ipc";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { Agents, useCreateAgentExecution, useSendToAgentExecution } from "@/queries/agent-queries";
import { useEditorURLQuery, useListEditors } from "@/queries/editor-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionLoading, agentExecutionName } from "@/utils/agent-executions";
import { formatError } from "@/utils/errors";
import { OpenInEditorKeyBinding, ToggleAISidebarKeyBinding } from "@/utils/platform";
import { VSCodeIPC } from "@/vscode-ipc";
import {
    AgentCodeContext,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
    AgentExecution_Phase,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

// Lazy load OnaWaiting to reduce initial bundle size
const OnaWaiting = lazy(() => import("@/components/ona/OnaWaiting").then((m) => ({ default: m.OnaWaiting })));
import { v4 as uuidv4 } from "uuid";

export const AgentExecutionConversation: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarContent, setSidebarContent] = useState<AgentMessageFragment | undefined>(undefined);
    const [sidebarViewMode, setSidebarViewMode] = useState<SidebarViewMode>("description");
    const panelRef = useRef<ImperativePanelHandle>(null);

    const { pendingMessages, handleNewMessage } = useOptimisticPendingMessages();
    const { error, messages, loadingMessage, todoItems, lastAgentBlockContext } = useStreamConversation(
        agentExecution.id,
        agentExecution?.status?.conversationUrl,
        pendingMessages,
    );
    const { handleStopAgentExecution } = useAgentExecutionActions(agentExecution);

    // Detect stuck conversations and report to Sentry.
    useStuckConversationDetection(agentExecution, lastAgentBlockContext);

    // Preload OnaWaiting component to avoid delay when showing empty state
    // By the time this component mounts, React is ready, so we can preload immediately
    useEffect(() => {
        void import("@/components/ona/OnaWaiting");
    }, []);

    // Update sidebar content when new thinking content arrives
    useEffect(() => {
        if (!sidebarContent || sidebarContent.type !== "thinking" || !messages.length) {
            return;
        }

        // Get the latest agent message
        const latestAgentMessage = messages[messages.length - 1];
        if (latestAgentMessage.type !== "agent") {
            return;
        }

        // Find the latest thinking fragment that matches the selected one's ID
        const matchingThinkingFragment = latestAgentMessage.fragments.find(
            (f): f is AgentMessageFragmentThinking => f.type === "thinking" && f.id === sidebarContent.id,
        );

        if (matchingThinkingFragment) {
            setSidebarContent(matchingThinkingFragment);
        }
    }, [messages, sidebarContent]);

    const handleCollapseClick = useCallback(() => {
        if (!panelRef.current) {
            return;
        }
        panelRef.current.collapse();
    }, []);

    const handleExpandClick = useCallback(() => {
        if (!panelRef.current) {
            return;
        }
        panelRef.current.expand(40);
    }, []);

    const handleContentClick = useCallback(
        (content: AgentMessageFragment) => {
            setSidebarViewMode("description");
            setSidebarContent(content);
            handleExpandClick();
        },
        [handleExpandClick],
    );

    useEscapeShortcut(() => {
        // Priority 1: Stop agent if it's running
        if (agentExecutionLoading(agentExecution)) {
            void handleStopAgentExecution();
            return;
        }
        // Priority 2: Close sidebar if open
        if (isSidebarOpen) {
            handleCollapseClick();
        }
    });

    useAISidebarToggleShortcut(() => {
        if (isSidebarOpen) {
            handleCollapseClick();
        } else {
            handleExpandClick();
        }
    });

    useOpenEditorShortcut(() => {
        if (isSidebarOpen) {
            // Toggle between vscode and description view
            setSidebarViewMode((prev) => (prev === "vscode" ? "description" : "vscode"));
        } else {
            // Open sidebar and set to vscode view
            setSidebarViewMode("vscode");
            handleExpandClick();
        }
    });

    const lastEnvironmentId = useMemo(() => {
        // TODO(ak) it is rather hack because we broke delivering user environment on time, remove after it is fixed
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            for (let j = message.fragments.length - 1; j >= 0; j--) {
                const fragment = message.fragments[j];
                if (fragment.type === "environmentCreation") {
                    return fragment.environmentId;
                }
            }
        }
        return agentExecution.status?.usedEnvironments[agentExecution.status?.usedEnvironments.length - 1]
            ?.environmentId;
    }, [agentExecution.status?.usedEnvironments, messages]);

    // Fetch data for VSCode browser view
    const { data: editors } = useListEditors();
    const { data: environment } = useEnvironment(lastEnvironmentId);
    const vscodeBrowserId = useMemo(
        () => editors?.editors.find((editor) => editor.alias === "vscode-browser")?.id,
        [editors?.editors],
    );
    const { data: editorUrl } = useEditorURLQuery({
        editorId: vscodeBrowserId,
        environment: environment,
    });

    const iframeSrc = useMemo(() => {
        let src = editorUrl?.url;
        if (
            environment?.status?.phase === EnvironmentPhase.STOPPING ||
            environment?.status?.phase === EnvironmentPhase.STOPPED ||
            environment?.status?.phase === EnvironmentPhase.DELETING ||
            environment?.status?.phase === EnvironmentPhase.DELETED
        ) {
            src = "https://vscode.gitpod.io";
        }
        return src;
    }, [editorUrl?.url, environment?.status?.phase]);

    return (
        <ResizablePanelGroup direction="horizontal" className="rounded-lg">
            <ResizablePanel defaultSize={100} minSize={20} className="flex w-full flex-col">
                <ConversationHeader
                    agentExecution={agentExecution}
                    isSidebarOpen={isSidebarOpen}
                    onExpand={handleExpandClick}
                    onCollapse={handleCollapseClick}
                />
                <ConversationError error={error} loadingMessage={loadingMessage} />
                <ConversationContent
                    messages={messages}
                    agentExecution={agentExecution}
                    onClick={handleContentClick}
                    todoItems={todoItems}
                    className="max-h-[60vh] min-h-0 flex-1"
                    data-tracking-id-none
                />
                <ConversationInput
                    hasMessages={messages.length > 0}
                    agentExecution={agentExecution}
                    onNewMessage={(message) => handleNewMessage(message, messages)}
                    todoItems={todoItems}
                    messages={messages}
                />
            </ResizablePanel>
            <ResizableHandle disabled={!isSidebarOpen} />
            <ResizablePanel
                ref={panelRef}
                defaultSize={0}
                minSize={30}
                collapsible
                className={cn("flex flex-col transition-all duration-100 ease-in-out", {
                    "mb-[1px] mr-[1px] rounded-r-lg border-l-0.5 border-border-base": isSidebarOpen,
                    "bg-surface-02 text-content-secondary": sidebarViewMode === "description",
                    "bg-surface-01": sidebarViewMode === "vscode",
                })}
                onExpand={() => setIsSidebarOpen(true)}
                onCollapse={() => setIsSidebarOpen(false)}
                data-testid="agent-execution-conversation-sidebar"
            >
                <SidebarHeader
                    content={sidebarContent}
                    viewMode={sidebarViewMode}
                    onViewModeChange={setSidebarViewMode}
                />
                <div className="flex-1 overflow-hidden" data-vscode-visible={sidebarViewMode === "vscode"}>
                    <div className={cn("h-full w-full", { hidden: sidebarViewMode !== "vscode" })}>
                        <VSCodeBrowserView editorUrl={iframeSrc} />
                    </div>
                    {sidebarViewMode === "description" && <SidebarContent content={sidebarContent} />}
                </div>
            </ResizablePanel>
        </ResizablePanelGroup>
    );
};

const agentExecutionInputAtomFamily = atomFamily((environmentId: string) =>
    atomWithStorage<string>(`environment-agent-input-${environmentId}`, ""),
);

// Component that looks exactly like ChatInput but creates conversations
const ChatInputStyled: FC<{ environmentId: string }> = ({ environmentId }) => {
    const createAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();
    const { toast } = useToast();

    const [inputValue, setInputValue] = useAtom(agentExecutionInputAtomFamily(environmentId));

    const handleSendMessage = useCallback(
        async (text: string) => {
            if (!text.trim()) return;

            try {
                // Create new conversation
                const { agentExecutionId } = await createAgentExecution.mutateAsync({
                    agentId: Agents.InEnvironmentOna.id,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: environmentId,
                        },
                    }),
                });

                // Send initial message
                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecutionId,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            input: {
                                case: "text",
                                value: new UserInputBlock_TextInput({
                                    content: text,
                                }),
                            },
                        }),
                    }),
                );

                // Clear input
                setInputValue("");
            } catch (error) {
                toast({
                    title: "Error starting conversation",
                    description: formatError(error),
                });
            }
        },
        [createAgentExecution, environmentId, sendToAgentExecution, setInputValue, toast],
    );

    const onKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                const input = e.target as HTMLTextAreaElement;
                e.preventDefault();
                e.stopPropagation();
                await handleSendMessage(input.value);
                return;
            }
        },
        [handleSendMessage],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const isInputEmpty = !inputValue.trim();

    return (
        <div
            className={cn(
                "relative flex w-full flex-col gap-1 rounded-lg border border-border-base bg-surface-pure pb-2 pl-4 pr-0 pt-4 shadow-sm",
            )}
        >
            <ChatInputWithPrompts
                minHeight={40}
                maxHeight={300}
                popoverSide="top"
                popoverSideOffset={20} // top padding + border + some spacing
                popoverWidthOffset={16} // account for the total padding (only left padding at the moment)
                popoverAlignOffset={-16} // account for the left padding
                placeholder="Describe your task in detail or type / for commands"
                onKeyDown={onKeyDown}
                onChange={handleChange}
                value={inputValue}
                data-testid="chat-input-styled"
                autoFocus
                enterKeyHint="send"
            />
            <div className="flex items-center gap-2 self-end pr-2">
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            Send
                            <div className="flex items-center gap-1">
                                <KeyboardKey className="bg-surface-invert-tertiary">Enter</KeyboardKey>
                            </div>
                        </div>
                    }
                >
                    <Button
                        id="send-button"
                        className="size-9 p-0"
                        LeadingIcon={IconArrowUp}
                        aria-label="Send message"
                        onClick={() => handleSendMessage(inputValue)}
                        disabled={isInputEmpty || sendToAgentExecution.isPending}
                        data-tracking-id="send-to-agent-execution-chat-input"
                    />
                </Tooltip>
            </div>
        </div>
    );
};

export type SidebarViewMode = "description" | "vscode";

export type VSCodeBrowserViewProps = {
    editorUrl: string | undefined;
    followMode?: FollowModeState;
    visible?: boolean;
} & PropsWithClassName;

export const VSCodeBrowserView: FC<VSCodeBrowserViewProps> = ({ editorUrl, className, followMode, visible = true }) => {
    const { vscodeIpc, setVscodeIpc } = useVSCodeIpcContext();
    const iframeSrc = editorUrl || "https://vscode.gitpod.io";

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const api = useGitpodAPI();
    const { effectiveTheme } = useTheme();

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }

        setVscodeIpc(new VSCodeIPC(api, iframeRef.current, effectiveTheme));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iframeSrc]);

    useEffect(() => {
        return () => {
            setVscodeIpc(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        void vscodeIpc.current?.setVisibilityState(visible);
    }, [visible, vscodeIpc]);

    const handleIframeLoad = useCallback(() => {
        if (!iframeRef.current?.contentWindow) {
            return;
        }

        if (followMode?.enabled) {
            void vscodeIpc.current?.enableFollowMode();
        }
        void vscodeIpc.current?.setVisibilityState(visible);
    }, [followMode?.enabled, vscodeIpc, visible]);

    return (
        <iframe
            ref={iframeRef}
            src={iframeSrc}
            className={cn("h-full w-full border-none", className)}
            sandbox="allow-same-origin allow-pointer-lock allow-scripts allow-downloads allow-forms allow-popups"
            allow="clipboard-read; clipboard-write; keyboard-map;"
            data-testid="vscode-browser-iframe"
            onLoad={handleIframeLoad}
            tabIndex={-1}
        />
    );
};

export const SidebarHeader: FC<{
    content?: AgentMessageFragment;
    viewMode: SidebarViewMode;
    onViewModeChange: (mode: SidebarViewMode) => void;
}> = ({ content, viewMode, onViewModeChange }) => {
    const text = useMemo(() => {
        if (!content) {
            return "No action selected";
        }
        switch (content.type) {
            case "action":
                return "Action";
            case "fileModification":
                return "File modification";
            case "environmentCreation":
                return "Environment creation";
            case "thinking":
                return "Thoughts";
            default:
                return `Unknown: ${content.type}`;
        }
    }, [content]);

    return (
        <div className="flex h-12 w-full items-center justify-between border-b border-border-base px-4 py-3 sm:px-6">
            <Heading1 className="shrink-0 text-lg">{text}</Heading1>
            <Tooltip
                content={
                    <div className="flex flex-row items-center gap-2">
                        <span>Toggle VS Code Browser</span>
                        <KeyboardCombo keys={OpenInEditorKeyBinding} className="bg-surface-invert-tertiary" />
                    </div>
                }
            >
                <div className="flex items-center gap-2">
                    <Text className="text-sm">VS Code Browser</Text>
                    <Switch
                        state={viewMode === "vscode" ? "checked" : "unchecked"}
                        onToggle={(checked) => onViewModeChange(checked ? "vscode" : "description")}
                        aria-label="Toggle VS Code Browser"
                    />
                </div>
            </Tooltip>
        </div>
    );
};

const LastUserMessageButton: FC<{
    messages: Message[];
}> = ({ messages }) => {
    const lastUserMessage = useMemo(() => {
        if (!messages || messages.length === 0) {
            return null;
        }
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].type === "user") {
                return messages[i] as UserMessage;
            }
        }
        return null;
    }, [messages]);

    const handleScrollToLastUserMessage = useCallback(() => {
        if (!lastUserMessage) {
            return;
        }

        // Find the user message element by data attribute anywhere in the document
        const userMessageElement = document.querySelector(`[data-message-id="user-${lastUserMessage.id}"]`);
        const scrollable = document.querySelector("#conversation-content");

        if (
            userMessageElement &&
            scrollable &&
            // to make the compiler happy - Element doesn't have offsetTop as querySelector might also return an SVGElement
            userMessageElement instanceof HTMLElement &&
            scrollable instanceof HTMLElement
        ) {
            // We're calling scrollTo on the container rather than userMessageElement.scrollIntoView as it doesn't allow
            // us to control what container to scroll (it's in the spec, but not supported yet)
            scrollable.scrollTo({
                // Scroll up 10px extra to add some padding before the message
                top: userMessageElement.offsetTop - scrollable.offsetTop - 10,
                behavior: "smooth",
            });
        }
    }, [lastUserMessage]);

    if (!lastUserMessage) {
        return null;
    }

    return (
        <Tooltip content="Scroll to last user message">
            <Button
                variant="link"
                size="sm"
                LeadingIcon={IconArrowUp}
                onClick={handleScrollToLastUserMessage}
                className="text-xs font-normal"
                data-tracking-id="scroll-to-last-user-message"
            >
                Last user message
            </Button>
        </Tooltip>
    );
};

export const SidebarContent: FC<{
    content?: AgentMessageFragment;
}> = ({ content }) => {
    const children = useMemo(() => {
        if (!content) {
            return <div className="font-mono text-sm">No content</div>;
        }
        switch (content.type) {
            case "action":
                return (
                    <div className="flex flex-col gap-2">
                        <div>
                            <Text className="text-sm font-bold">Intent</Text>
                            <div className="font-mono text-sm">{content.intentDescription}</div>
                        </div>
                        <div>
                            <Text className="text-sm font-bold">Result</Text>
                            <div className="font-mono text-sm">{content.resultDescription || "No result"}</div>
                        </div>
                    </div>
                );
            case "fileModification":
                return <div>{content.content}</div>;
            case "environmentCreation":
                return <div>{content.environmentId}</div>;
            case "thinking":
                return (
                    <div className="flex flex-col gap-2">
                        <div className="whitespace-pre-wrap font-mono text-sm">{content.content}</div>
                    </div>
                );
            default:
                return <div>{content.type}</div>;
        }
    }, [content]);

    return (
        <ScrollArea orientation="both">
            <div className="px-4 py-3 sm:px-6">{children}</div>
        </ScrollArea>
    );
};

const ConversationHeader: FC<{
    agentExecution: PlainAgentExecution;
    isSidebarOpen: boolean;
    onExpand: () => void;
    onCollapse: () => void;
}> = ({ agentExecution, isSidebarOpen, onExpand, onCollapse }) => {
    const displayName = useMemo(() => agentExecutionName(agentExecution), [agentExecution]);

    return (
        <div className="flex h-12 w-full items-center justify-between gap-2 border-b border-border-base px-4 py-2">
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : (
                    <div className="flex min-w-0 max-w-xs items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            <div className="flex min-w-14 shrink items-center justify-end gap-1">
                <AgentExecutionActiveEnvironment agentExecution={agentExecution} />
                <Tooltip
                    content={
                        <div className="flex flex-row items-center gap-2">
                            <span>{isSidebarOpen ? "Close sidebar" : "Open sidebar"}</span>
                            <KeyboardCombo keys={ToggleAISidebarKeyBinding} className="bg-surface-invert-tertiary" />
                        </div>
                    }
                >
                    <Button
                        variant="ghost"
                        className="rounded-[4px] border-0 p-0"
                        size="lg"
                        onClick={isSidebarOpen ? onCollapse : onExpand}
                        LeadingIcon={isSidebarOpen ? IconPanelLeftOpen : IconPanelLeftClose}
                        aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                        data-tracking-id="toggle-sidebar-agent-execution-header"
                    />
                </Tooltip>
            </div>
        </div>
    );
};

export const ConversationError: FC<{
    error?: Error;
    loadingMessage?: string;
}> = ({ error, loadingMessage }) => {
    // streamWithCache currently returns a Error which doesn't include the status code
    // Ideally we'll modify the agent runtime so it doesn't populate the conversation URL until it's ready
    const is404 = error?.message?.includes("404");

    if (is404) {
        return null;
    }

    if (!error && !loadingMessage) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1">
            {error && <Banner text={formatError(error)} variant="danger" className="mx-4 mb-0 mt-4 sm:mx-6" />}
            {loadingMessage && (
                <Text className="w-full text-center text-sm text-content-secondary">{loadingMessage}</Text>
            )}
        </div>
    );
};

export const ConversationContent: FC<{
    messages: Message[];
    agentExecution: PlainAgentExecution | null;
    onClick?: (content: AgentMessageFragment) => void;
    className?: string;
    environmentId?: string;
    todoItems?: AgentMessageFragmentTodoItem[];
}> = ({ messages, agentExecution, onClick, className, environmentId, todoItems }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const scrollAreaRef = useRef<ScrollAreaHandle>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [, setInputValue] = useAtom(agentExecutionInputAtomFamily(environmentId ?? ""));
    const scrollButtonTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const wasAtBottomRef = useRef(true);

    const handleScrollToBottom = useCallback(() => {
        scrollAreaRef.current?.scrollToBottom();
    }, []);

    const handleScrollStateChange = useCallback((isAtBottom: boolean) => {
        const wasAtBottom = wasAtBottomRef.current;
        wasAtBottomRef.current = isAtBottom;

        // Clear any existing timeout
        if (scrollButtonTimeoutRef.current) {
            clearTimeout(scrollButtonTimeoutRef.current);
            scrollButtonTimeoutRef.current = null;
        }

        if (!isAtBottom) {
            // Only apply delay when transitioning from bottom to not-bottom
            // This prevents the button from showing during auto-scroll
            if (wasAtBottom) {
                scrollButtonTimeoutRef.current = setTimeout(() => {
                    setShowScrollButton(true);
                }, 500);
            } else {
                // Already not at bottom, show immediately
                setShowScrollButton(true);
            }
        } else {
            // Hide immediately when at bottom
            setShowScrollButton(false);
        }
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollButtonTimeoutRef.current) {
                clearTimeout(scrollButtonTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="relative flex h-full min-h-0 flex-1 flex-col">
            <ScrollArea
                ref={scrollAreaRef}
                orientation="vertical"
                autoScroll={true}
                autoScrollContentRef={contentRef}
                scrollRestorationKey={agentExecution?.id}
                id="conversation-content"
                className={cn("mx-[2px] my-[2px] flex-1 pb-8 pl-[var(--scrollbar-width)]", className)}
                onScrollStateChange={handleScrollStateChange}
            >
                {agentExecution ? (
                    <div ref={contentRef}>
                        <ChatMessages
                            agentExecution={agentExecution}
                            messages={messages}
                            onClick={onClick}
                            todoItems={todoItems}
                            data-tracking-id-none
                        />
                    </div>
                ) : (
                    <div className="flex h-full min-h-[200px] items-center justify-center">
                        <div className="flex flex-col items-center text-center">
                            <Suspense fallback={<OnaLettermark className="mb-6 size-8 text-content-primary" />}>
                                <OnaWaiting className="mb-6" />
                            </Suspense>
                            <Heading1 className="text-wrap">What do you want to get done today?</Heading1>
                            <p className="text-lg text-content-muted">Here are some suggestions to get you started</p>

                            <div className="mt-8 flex flex-wrap justify-center gap-2">
                                {PREDEFINED_TASKS.map((task) => (
                                    <PromptTemplate
                                        key={task.taskName}
                                        Icon={task.Icon}
                                        iconColor={task.iconColor}
                                        iconBackgroundColor={task.iconBackgroundColor}
                                        title={task.title}
                                        description={task.description}
                                        selected={false}
                                        data-tracking-id={`prompt-template-${task.taskName}-conversation`}
                                        onSelectChange={() => {
                                            setInputValue(task.prompt);
                                        }}
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </ScrollArea>
            <ScrollToBottomButton
                onClick={handleScrollToBottom}
                visible={showScrollButton}
                data-tracking-id-none // tracking is handled by the button inside ScrollToBottomButton
            />
        </div>
    );
};

export const ConversationInput: FC<{
    agentExecution: PlainAgentExecution | null;
    hasMessages: boolean;
    onNewMessage: (message: NewPendingMessage) => void;
    todoItems: TodoItem[];
    followMode?: FollowModeState;
    messages: Message[];
    environmentId?: string;
}> = ({ agentExecution, hasMessages, onNewMessage, todoItems, followMode, messages, environmentId }) => {
    const { value: isTodoGroupingEnabled } = useFlagTodoGroupingEnabled();

    // Determine if Ona is currently working
    const isOnaWorking =
        agentExecution &&
        agentExecution.status?.phase !== AgentExecution_Phase.WAITING_FOR_INPUT &&
        agentExecution.status?.phase !== AgentExecution_Phase.STOPPED;

    const placeholder = isOnaWorking ? "Add a follow up task or command" : "Describe your task or type / for commands";

    return (
        <div className="mt-0 flex-shrink-0">
            <div className={cn("bg-surface-default flex justify-center")}>
                <div className="relative flex w-full max-w-[700px] flex-shrink-0 flex-col items-center px-4 pb-4 sm:px-6">
                    <div className="absolute bottom-full h-8 w-full max-w-[95%] bg-gradient-to-t from-surface-01 to-transparent" />

                    {agentExecution ? (
                        <div className={cn("w-full rounded-xl bg-surface-muted")}>
                            {!isTodoGroupingEnabled && <TodoList todos={todoItems} agentExecution={agentExecution} />}
                            <div className={cn("px-1.5 pb-1.5", { "p-1.5": isTodoGroupingEnabled })}>
                                <ChatInput
                                    hasMessages={hasMessages}
                                    agentExecution={agentExecution}
                                    onNewMessage={onNewMessage}
                                    placeholder={placeholder}
                                    followMode={followMode}
                                    autoFocus
                                />
                            </div>
                        </div>
                    ) : environmentId ? (
                        <>
                            <div className={cn("w-full rounded-xl bg-surface-muted")}>
                                <div className="p-1.5">
                                    <ChatInputStyled environmentId={environmentId} />
                                </div>
                            </div>
                        </>
                    ) : null}

                    {agentExecution && (
                        <div className="mt-1 flex w-full items-center justify-between">
                            <LastUserMessageButton messages={messages} />
                            {isTodoGroupingEnabled && (
                                <div className="ml-auto">
                                    <StopButton agentExecution={agentExecution} />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
